/**
 * DmsElectronicSignature - Component for electronic signature in critical operations
 *
 * Uses the logged user's credentials to confirm critical actions like:
 * - Dispensing medications
 * - Prescribing medications
 * - Approving institutions/users
 * - Other critical operations
 */
import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Paper,
    Avatar,
    Alert,
    CircularProgress,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import DrawIcon from "@mui/icons-material/Draw";
import {
    DmsModal,
    DmsButton,
    getToken,
    loginUser,
    HTTP_STATUS,
    Permissions,
    Notifications,
    log,
    LogLevel,
    LogType,
    intl,
} from "dmsbasereact";

/**
 * @param {boolean} show - Controls modal visibility
 * @param {function} setShow - Function to set modal visibility
 * @param {function} onSuccess - Callback on successful authentication
 * @param {function} onClose - Callback when modal is closed
 * @param {string} title - Modal title (default: "Electronic Signature")
 * @param {string} actionDescription - Description of the action being signed
 * @param {string} logModule - Module name for logging
 * @param {any} data - Additional data passed to onSuccess
 * @param {array} additionalPermissions - Additional permissions required
 * @param {boolean} showSuccessToast - Whether to show success toast (default: false)
 */
export const DmsElectronicSignature = ({
    show,
    setShow,
    onSuccess = null,
    onClose = null,
    title = null,
    actionDescription = null,
    logModule = "",
    data = null,
    additionalPermissions = [],
    showSuccessToast = false,
}) => {
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Get logged user from token
    const token = getToken();
    const userLogin = token?.login || token?.sub || "";
    const userName = token?.name || userLogin;

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setPassword("");
            setError(null);
        }
    }, [show]);

    // Handle close
    const handleClose = useCallback(() => {
        setPassword("");
        setError(null);
        setShow(false);
        if (onClose) {
            onClose();
        }
    }, [setShow, onClose]);

    // Handle submit
    const handleSubmit = useCallback(async () => {
        if (!password.trim()) {
            setError(intl.get("PasswordRequired").d("Password is required"));
            return;
        }

        setError(null);
        setIsLoading(true);

        // Build permissions list inside callback to avoid dependency issues
        const requiredPermissions = [
            ...additionalPermissions,
            Permissions.CAN_DOUBLE_BLIND,
        ];

        try {
            const authResponse = await loginUser({
                login: userLogin,
                password: password,
                authentication_url: "/token",
                remember: false,
            });

            if (authResponse.status === HTTP_STATUS.OK_200) {
                const scopes = Array.isArray(authResponse?.data?.scopes)
                    ? authResponse.data.scopes
                    : [];

                const hasAllPermissions = requiredPermissions.every((perm) =>
                    scopes.includes(perm)
                );

                if (hasAllPermissions) {
                    // Log the action
                    log(
                        actionDescription || intl.get("ElectronicSignature").d("Electronic Signature"),
                        logModule,
                        LogLevel.INFO,
                        LogType.USER,
                        null,
                        getToken(),
                        userLogin
                    );

                    // Close modal
                    setShow(false);

                    // Show success toast only if explicitly requested
                    if (showSuccessToast) {
                        Notifications.toast(
                            intl.get("SignatureSuccess").d("Signature confirmed successfully"),
                            Notifications.TYPE_SUCCESS
                        );
                    }

                    // Call success callback
                    if (onSuccess) {
                        onSuccess(data);
                    }
                } else {
                    setError(intl.get("UserWithoutPermission").d("User does not have the required permissions"));
                }
            } else if (authResponse.status === HTTP_STATUS.UNAUTHORIZED_401) {
                setError(intl.get("InvalidPassword").d("Invalid password"));
            } else {
                setError(authResponse?.data?.detail || intl.get("AuthenticationError").d("Authentication failed"));
            }
        } catch (err) {
            console.error("Electronic signature error:", err);
            setError(intl.get("AuthenticationError").d("Authentication failed"));
        } finally {
            setIsLoading(false);
        }
    }, [password, userLogin, additionalPermissions, actionDescription, logModule, setShow, onSuccess, data, showSuccessToast]);

    // Handle key press
    const handleKeyPress = (event) => {
        if (event.key === "Enter" && !isLoading) {
            handleSubmit();
        }
    };

    const modalTitle = title || intl.get("ElectronicSignature").d("Electronic Signature");

    return (
        <DmsModal isVisible={show} onClose={handleClose}>
            <DmsModal.Header title={modalTitle} onClose={handleClose} />
            <DmsModal.Body>
                <Box sx={{ p: 2 }}>
                    {/* Header with icon */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Avatar
                            sx={{
                                width: 64,
                                height: 64,
                                mx: "auto",
                                mb: 2,
                                bgcolor: "primary.main",
                            }}
                        >
                            <DrawIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("ElectronicSignatureDescription").d(
                                "Enter your password to confirm this action with your electronic signature."
                            )}
                        </Typography>
                    </Box>

                    {/* Action description */}
                    {actionDescription && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: "grey.50",
                                borderColor: "grey.200",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                {intl.get("ActionToSign").d("Action to sign")}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-line' }}>
                                {actionDescription}
                            </Typography>
                        </Paper>
                    )}

                    {/* Error message */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* User info (read-only) */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            bgcolor: "primary.50",
                            borderColor: "primary.100",
                        }}
                    >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                            <PersonIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                {intl.get("SigningAs").d("Signing as")}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userLogin}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Password field */}
                    <TextField
                        fullWidth
                        type="password"
                        label={intl.get("Password").d("Password")}
                        placeholder={intl.get("EnterPasswordToSign").d("Enter your password to sign")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1 }}
                    />

                    <Typography variant="caption" color="text.secondary">
                        {intl.get("SignatureSecurityNote").d(
                            "Your signature will be recorded with timestamp for audit purposes."
                        )}
                    </Typography>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <DmsButton
                    variant="secondary"
                    text={intl.get("Cancel").d("Cancel")}
                    onClick={handleClose}
                    disabled={isLoading}
                />
                <DmsButton
                    variant="primary"
                    text={isLoading ? "" : intl.get("SignAndConfirm").d("Sign & Confirm")}
                    onClick={handleSubmit}
                    disabled={isLoading || !password.trim()}
                    icon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DrawIcon />}
                />
            </DmsModal.Footer>
        </DmsModal>
    );
};

export default DmsElectronicSignature;
